import { useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography } from '@altermaind/web/ui';
import { Charts } from '@/_shared/components/Charts';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getEmployeesMeCall } from '@/profile/state/api';
import { getSurveyCampaignByIdResponsesResultsStatsCall } from '@/surveys/state';

export const SurveyTeamResultsVsDepartmentAndOrganization = () => {
  const { hasManagerLeader, hasTeamLeader } = getEmployeesMeCall.hook();
  const onlyTeamAndDepartmentCompare = hasTeamLeader && !hasManagerLeader;
  const t = useTranslations(
    'departments.tabs.team.assessment.resultsTeamVsDepartmentVsOrganization',
  );

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();

  const { teamStats, departmentsStats, organizationStats } =
    getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const data = useMemo(() => {
    if (!departmentsStats?.[0].metricsScore || !teamStats.metricsScore) return [];

    const teamMetricsMap = new Map<string, number>();
    teamStats.metricsScore.forEach(orgMetric => {
      if (orgMetric.metric?.id) {
        teamMetricsMap.set(orgMetric.metric.id, orgMetric.score || 0);
      }
    });

    const orgMetricsMap = new Map<string, number>();
    organizationStats?.metricsScore?.forEach(orgMetric => {
      if (orgMetric.metric?.id) {
        orgMetricsMap.set(orgMetric.metric.id, orgMetric.score || 0);
      }
    });

    return departmentsStats?.[0].metricsScore.map(depMetric => {
      const valueTeam = teamMetricsMap.get(depMetric.metric?.id || '') || 0;
      const valueOrg = orgMetricsMap.get(depMetric.metric?.id || '') || 0;
      const valueDepartment = Number(depMetric?.score ?? 0);
      const diff = valueTeam - valueDepartment;
      const diffOrg = valueTeam - Number(valueOrg);
      // eslint-disable-next-line no-nested-ternary
      const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
      // eslint-disable-next-line no-nested-ternary
      const signOrg = diffOrg > 0 ? '+' : diffOrg < 0 ? '-' : '';

      return {
        name: depMetric.metric?.name || '',
        value: valueTeam,
        label:
          t('labelTooltipChart', {
            current: valueTeam,
            depValue: `${sign}${Math.abs(diff) === 0 ? 0 : Math.abs(diff).toFixed(1)} pt`,
            orgValue: `${signOrg}${Math.abs(diffOrg) === 0 ? 0 : Math.abs(diffOrg).toFixed(1)} pt`,
          }) || '',
        value2: valueDepartment || 0,
        value3: onlyTeamAndDepartmentCompare ? undefined : valueOrg,
      };
    });
  }, [
    departmentsStats,
    organizationStats?.metricsScore,
    teamStats,
    t,
    onlyTeamAndDepartmentCompare,
  ]);

  if (
    Array.isArray(departmentsStats?.[0]?.metricsScore) &&
    departmentsStats?.[0].metricsScore.length > 0
  ) {
    return (
      <Box>
        <Typography variant='h3' mb='xxxs'>
          {t('title')}
        </Typography>
        <Typography variant='p3/light'>{t('description')}</Typography>
        <Box mt='xxl' display='flex' flexDirection='row' alignItems='stretch'>
          <Box flex='1' mr='s' display='flex' alignItems='center'>
            <Charts.BarChartComponent
              domain={[0, 7]}
              hasMultipleValues
              domainHasInPercentage={false}
              data={data}
              defaultItemsSize={isDesktop ? 6 : 2}
            />
          </Box>
        </Box>
        <Box mt='s' display='flex' justifyContent='center'>
          <Box
            justifyContent='start'
            width='100%'
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
          >
            <Box alignContent='center' display='flex' alignItems='center' mr='m'>
              <Box
                width='xxs'
                height='xxs'
                backgroundColor='primary'
                borderRadius='50%'
                mr='xxxs'
              />
              <Typography variant='p2/light'>
                {t('legend.teamAverage', {
                  total: teamStats?.totalCompletedSurveys?.toString() || '',
                })}
              </Typography>
            </Box>
            <Box alignContent='center' display='flex' alignItems='center' mr='m'>
              <Box
                width='xxs'
                height='xxs'
                backgroundColor={'rgba(176, 128, 255, 1)' as never}
                borderRadius='50%'
                mr='xxxs'
              />
              <Typography variant='p2/light'>
                {t('legend.depAverage', {
                  total: departmentsStats?.[0]?.totalCompletedSurveys?.toString() || '',
                })}
              </Typography>
            </Box>
            {!onlyTeamAndDepartmentCompare ? (
              <Box alignContent='center' display='flex' alignItems='center' mr='m'>
                <Box
                  width='xxs'
                  height='xxs'
                  backgroundColor='white'
                  borderRadius='50%'
                  mr='xxxs'
                />
                <Typography variant='p2/light'>
                  {t('legend.orgAverage', {
                    total: organizationStats?.totalCompletedSurveys?.toString() || '',
                  })}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};
